import { Box, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import TimeAtEmployment, { TimeAtEmploymentValues } from '../../../../components/TimeAtEmployment';
import Input from '../../../../components/formComponents/Input';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import {
  EmploymentStatusEnum,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { usePrequalification, useStep, useTempInfo } from '../../../../hooks';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import { notRequiredString } from '../../../../utils/strings';
import { customerPreviousEmploymentValidation } from '../../../../utils/validation/customerInformation';
import {
  monthsValidation,
  numberOptionalValidation,
  numberRequiredValidation,
} from '../../../../utils/validation/numbers';
import { phoneValidationRequired } from '../../../../utils/validation/phoneNumber';
import { PreviousEmploymentInfo } from '../../../LoanApp/utils';

type EmploymentInfo = {
  employer_phone_number: string;
  years_at_job: number | undefined;
  months_at_job: number | undefined;
} & PreviousEmploymentInfo;

const InitialValues: EmploymentInfo = {
  employer_phone_number: '',
  years_at_job: undefined,
  months_at_job: undefined,
  previous_employer_name: '',
  previous_employer_phone_number: '',
  previous_job_title: '',
  previous_years_at_job: 0,
  previous_months_at_job: 0,
};

const AffiliateSinglePageEmploymentDetails = () => {
  const step = useStep();
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const { info, infoLoading } = useTempInfo();

  const {
    shouldExecutePrequalification,
    consentLoading,
    termsLoading,
    executePrequalification,
    savePrivacyConsents,
    ConsentContent,
    TermsAndConditionsModal,
  } = usePrequalification({ state: info?.data?.state });

  const handleSubmit = async (values: EmploymentInfo) => {
    const { data: tempInfo } = await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            ...values,
          },
        },
      },
    });

    const updatedTempInfoData = tempInfo?.temporaryInfoUpdate?.data;

    if (!(await savePrivacyConsents())) {
      return;
    }

    if (shouldExecutePrequalification && updatedTempInfoData) {
      await executePrequalification(updatedTempInfoData);
    }

    step.moveNext();
  };

  return (
    <Formik
      initialValues={InitialValues}
      validationSchema={Yup.object().shape({
        employer_phone_number: Yup.string().when('employment_status', {
          is: (employment_status: EmploymentStatusEnum) =>
            employment_status === EmploymentStatusEnum.Employed,
          then: phoneValidationRequired,
          otherwise: notRequiredString,
        }),
        years_at_job: numberRequiredValidation,
        months_at_job: Yup.number().when('years_at_job', {
          is: (years_at_job: number) => years_at_job <= 1,
          then: monthsValidation,
          otherwise: numberOptionalValidation,
        }),
        ...customerPreviousEmploymentValidation,
      })}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <Box>
            <Input
              label="EMPLOYER PHONE"
              name="employer_phone_number"
              mask="(999) 999-9999"
              type="tel"
              _container={{ w: 'full', mt: 4 }}
            />
            <TimeAtEmployment values={values as TimeAtEmploymentValues} mt={4} />
          </Box>
          <Flex flexDir="column" gap={4} mt={8} alignItems="center">
            {ConsentContent}
            <PrimaryButton
              trackClick={
                shouldExecutePrequalification
                  ? 'affiliate_single_page_consent_and_submit'
                  : 'affiliate_single_page_submit'
              }
              type="submit"
              loading={isSubmitting}
              isDisabled={isSubmitting || infoLoading || consentLoading || termsLoading}
            >
              SUBMIT
            </PrimaryButton>
          </Flex>

          {TermsAndConditionsModal}
        </Form>
      )}
    </Formik>
  );
};

export default AffiliateSinglePageEmploymentDetails;
